import WhatsappIcon from '@mui/icons-material/WhatsApp'
import { Box, Icon, IconButton, styled, Typography } from '@mui/material'
import Card from '@mui/material/Card'
import { History } from 'history'
import React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import Navigator, { TNavigatorItem } from '@/app/component/atom/navigator'
import PopupEditableText from '@/app/component/atom/popup-editable-text'
import StyledBadge from '@/app/component/atom/styled-badge'
import CustomTooltip from '@/app/component/atom/tooltip'
import Loading from '@/app/component/guard/loading'
import SidebarLayout from '@/app/component/layout/sidebar-layout'
import createHydrated from '@/app/component/wrapper/hydratable'
import { cannot } from '@/app/helpers'
import { FilesState } from '@/app/module/campaigns/types'
import { useGetSenderOptions } from '@/app/module/common/hooks/use-get-sender-options'
import { useGetPhoneNumber } from '@/app/module/phonenumbers/hooks/use-get-phonenumber'
import { useUpatePhoneNumber } from '@/app/module/phonenumbers/hooks/use-update-phonenumber'
import { ChannelType, PhoneNumber } from '@/app/module/phonenumbers/types'
import { formatPhoneNumber } from '@/app/service/util'
import {
  CountryTimezone,
  FileType,
  FileUploadType,
  PersonalizationType,
  TimezoneWithCountryCodeType,
} from '@/app/types'

import InboundVoiceCampaign from './campaign-hook-tab'
import SmsHooksTab from './sms-hooks-tab'
import UnsavedRulesProvider from './unsaved-rules.context'
import VoiceHookTab from './voice-hook-tab'
import WhatsappHooksTab from './whatsapp-hooks-tab'

type Props = {
  history: History
  tabName: ChannelType
  countriesTimezones: CountryTimezone[]
  countryCode: string
  files: FilesState
  orgId: number
  personalizationList: PersonalizationType
  phoneNumberId: number
  timezone: string
  timezones: TimezoneWithCountryCodeType
  token: string

  getFile: (payload: { file: string; msgId: string; orgId: number; token: string }) => Promise<FileType>
  onError: (payload: { message: string }) => void
  onMicAccessError: () => void
  saveFile: (payload: { item: FileUploadType; msgId: string; orgId: number; token: string }) => Promise<FileType>
}

const PhoneNumberRules: React.FC<Props> = (props) => {
  const navigate = useNavigate()
  const { orgId, token, phoneNumberId, tabName: tab } = props
  const { data, error, isFetching } = useGetPhoneNumber(phoneNumberId)
  const [updatePhoneNumber, { isLoading }] = useUpatePhoneNumber()
  const { senderIds, isLoading: senderIdsLoading } = useGetSenderOptions()

  const phoneNumber = data as PhoneNumber | undefined

  React.useEffect(() => {
    if (error && 'status' in error && error.status === 404) {
      navigate('/phonenumbers')
    }
  }, [error, navigate])

  const generateTabCount = React.useCallback(
    (type: ChannelType) => {
      switch (type) {
        case ChannelType.Sms:
          return phoneNumber?.hooks?.length || 0
        case ChannelType.WhatsApp:
          return phoneNumber?.whatsappHooks?.length || 0
        case ChannelType.Voice:
          return phoneNumber?.callHooks?.length || 0
        case ChannelType.Campaign:
          return phoneNumber?.inboundCallCampId ? 1 : 0
        default:
          return 0
      }
    },
    [phoneNumber],
  )

  const availableTabs = React.useMemo(
    () =>
      tabNames.filter(({ value }) => {
        switch (value) {
          case ChannelType.Sms:
            return phoneNumber?.sms
          case ChannelType.WhatsApp:
            return phoneNumber?.whatsApp
          case ChannelType.Campaign:
          case ChannelType.Voice:
            return phoneNumber?.voice
          default:
            return cannot(value)
        }
      }),
    [phoneNumber],
  )

  const items: TNavigatorItem<ChannelType>[] = React.useMemo(
    () =>
      availableTabs.map(({ icon, label, value }) => ({
        label,
        path: value,
        icon: value !== 'whatsapp' ? icon : (color) => <WhatsappIcon color={color} />,
        endAdornment: (
          <StyledBadge color="primary" badgeContent={generateTabCount(value)} showZero>
            <span />
          </StyledBadge>
        ),
      })),
    [availableTabs, generateTabCount],
  )

  const handleItemClick = React.useCallback(
    (path: ChannelType) => {
      if (path === tab) return
      navigate(`/phonenumbers/${phoneNumberId}/${path}`)
    },
    [phoneNumberId, tab, navigate],
  )

  const sidebarSubtitle = (
    <>
      <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
        <CustomTooltip title={formatPhoneNumber(`+${phoneNumber?.phoneNumber || ''}`)}>
          <Typography
            color="textSecondary"
            noWrap
            variant="subtitle1"
            sx={{ display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {formatPhoneNumber(`+${phoneNumber?.phoneNumber || ''}`)}
          </Typography>
        </CustomTooltip>
      </Box>
      {phoneNumber && (
        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <PopupEditableText
            maxLength={100}
            text={phoneNumber.name}
            title="Edit Name"
            onSubmit={async (val) => {
              await updatePhoneNumber(phoneNumberId, { name: val })
            }}
          >
            <CustomTooltip title={phoneNumber.name}>
              <Typography
                color="textSecondary"
                noWrap
                variant="subtitle1"
                sx={{ display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {phoneNumber.name}
              </Typography>
            </CustomTooltip>
          </PopupEditableText>
        </Box>
      )}
    </>
  )

  const sidebarTitle = (
    <Title>
      <IconButton size="small" onClick={() => navigate('/phonenumbers')}>
        <Icon>chevron_left</Icon>
      </IconButton>
      Phone Number
    </Title>
  )

  const sidebar = (
    <Navigator
      activePath={tab}
      id="phonenumber"
      items={items}
      title={sidebarTitle}
      subtitle={sidebarSubtitle}
      onItemClick={handleItemClick}
    />
  )

  const content = (
    <ContainerCard>
      <ContentScroll>
        {tab === ChannelType.Sms && <SmsHooksTab {...props} phoneNumberId={phoneNumberId} senderIds={senderIds} />}
        {tab === ChannelType.WhatsApp && (
          <WhatsappHooksTab {...props} phoneNumberId={phoneNumberId} senderIds={senderIds} />
        )}
        {tab === ChannelType.Voice && <VoiceHookTab {...props} phoneNumberId={phoneNumberId} senderIds={senderIds} />}
        {tab === ChannelType.Campaign && (
          <InboundVoiceCampaign orgId={orgId} phoneNumberId={phoneNumberId} token={token} />
        )}
      </ContentScroll>
    </ContainerCard>
  )

  return (
    <UnsavedRulesProvider key={phoneNumberId}>
      <Loading isLoading={isFetching || isLoading || senderIdsLoading}>
        {phoneNumber && <SidebarLayout sidebar={sidebar} content={content} />}
      </Loading>
    </UnsavedRulesProvider>
  )
}

const ContainerCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
}))

const ContentScroll = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
})

export const tabNames = [
  {
    icon: 'sms',
    label: 'SMS Rules',
    value: ChannelType.Sms,
  },
  {
    icon: 'whatsapp',
    label: 'WhatsApp Rules',
    value: ChannelType.WhatsApp,
  },
  {
    icon: 'call',
    label: 'Voice Rules',
    value: ChannelType.Voice,
  },
  {
    icon: 'campaign',
    label: 'Voice Campaign',
    value: ChannelType.Campaign,
  },
]

const Title = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: -8,
  marginRight: -8,
})

export default createHydrated(PhoneNumberRules)
